import styled, { css } from 'styled-components/macro';
import {
  FONT_CASE_KEYWORD,
  FONT_WEIGHT_KEYWORD,
  FontSizes,
  GRADIENT_TYPES,
  LP_DESIGN_COMMON_COLORS,
  TEXT_ALIGNMENT,
  TEXT_ORIENTATION,
} from '../../constants';
import { ILandingPageBuilderForm } from '../../types';
import { defaultFontFamilyOptions } from 'app/pages/design/landingPageBuilder/components';
import { IImgProps } from 'lib/components/formik/FormikImageUpload/types';

export const scaleFontForPreview = (fontSize: number): number => {
  const previewSize = { width: 814, height: 634 };
  const fullScreenSize = {
    width: window.screen.width,
    height: window.screen.height,
  };
  const fullDiagonal = Math.sqrt(
    fullScreenSize.width ** 2 + fullScreenSize.height ** 2
  );
  const previewDiagonal = Math.sqrt(
    previewSize.width ** 2 + previewSize.height ** 2
  );

  const scaleRatio = previewDiagonal / fullDiagonal;
  const tweakFactor = 0.7;
  return fontSize * scaleRatio * tweakFactor;
};

export function scaleFontSizesInHtml(html?: string, scale?: number): string {
  if (!html || !scale) return html || '';
  return html.replace(/font-size:\s*(\d+(\.\d+)?)px/gi, (_, size) => {
    const scaled = Math.round(parseFloat(size) * scale);
    return `font-size: ${scaled}px`;
  });
}

// LP PREVIEW
export const LandingPageDesignPreviewContainer = styled.div<{
  fontFamily: string;
}>`
  width: 100%;
  display: flex;
  padding: 24px 0;
  height: calc(100vh - 149px);
  box-sizing: border-box;
  overflow-y: auto;
  flex-direction: column;
  align-items: center;
`;
export const LandingPageDesignPreviewWrapper = styled.div<{
  isMobile: boolean;
}>`
  width: ${({ isMobile }) => (isMobile ? '247px' : '814px')};
`;

// HEADER
export const HeaderContainer = styled.div<{
  headerBackgroundColor: string;
  showHeaderShadow: number;
  isMobile: boolean;
  screenshot: boolean;
}>`
  display: flex;
  justify-content: space-between;
  width: 100%;
  min-height: 97px;
  box-sizing: border-box;
  flex-shrink: 0;

  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  align-items: ${({ isMobile }) => (isMobile ? 'start' : 'center')};
  padding-top: ${({ isMobile }) => (isMobile ? '9px' : '28px')};
  padding-bottom: ${({ isMobile }) => (isMobile ? '16px' : '28px')};
  padding-left: ${({ isMobile }) => (isMobile ? '11px' : '44px')};
  padding-right: ${({ isMobile }) => (isMobile ? '21px' : '44px')};
  background: ${({ headerBackgroundColor }) =>
    headerBackgroundColor
      ? headerBackgroundColor
      : props => props.theme.colors.neutral[40]};

  border-bottom: ${({ screenshot, showHeaderShadow, theme }) =>
    screenshot && showHeaderShadow && `3px solid ${theme.colors.neutral[10]}`};

  position: relative;
  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 6px;
    pointer-events: none;
    box-shadow: ${({ showHeaderShadow }) =>
      showHeaderShadow ? '0 2px 4px -1px rgba(0, 0, 0, 0.12)' : 'none'};
    background: transparent;
  }
`;

export const LabelContainer = styled.div<{
  headerContentOrientation: TEXT_ORIENTATION;
  headerContentAlignment: TEXT_ALIGNMENT;
  isMobile: boolean;
}>`
  display: flex;
  margin-left: ${({ isMobile }) => (isMobile ? '0px' : 'auto')};
  align-self: ${({ isMobile }) => (isMobile ? 'start' : 'center')};
  flex-direction: ${({ headerContentOrientation }) =>
    headerContentOrientation === TEXT_ORIENTATION.HORIZONTAL
      ? 'row'
      : 'column'};
  align-items: ${({ headerContentAlignment }) =>
    headerContentAlignment === TEXT_ALIGNMENT.RIGHT
      ? 'flex-end'
      : headerContentAlignment === TEXT_ALIGNMENT.LEFT
        ? 'flex-start'
        : TEXT_ALIGNMENT.CENTER};
  gap: ${({ headerContentOrientation }) =>
    headerContentOrientation === TEXT_ORIENTATION.HORIZONTAL ? '20px' : '7px'};
`;

export const Label = styled.div<{
  color: string;
  headerFontCase: FONT_CASE_KEYWORD;
  headerFontWeight: FONT_WEIGHT_KEYWORD;
  headerFontSize: number;
  headerFontFamily: string;
  headerLineHeight: number;
}>`
  letter-spacing: 0px;

  color: ${({ color }) => color};
  font-family: ${({ headerFontFamily }) =>
    headerFontFamily || defaultFontFamilyOptions[0].value};
  font-size: ${({ headerFontSize }) =>
    headerFontSize ? scaleFontForPreview(headerFontSize) + 'px' : '12px'};
  line-height: ${({ headerLineHeight }) =>
    `${scaleFontForPreview(headerLineHeight)}px`};
  text-transform: ${({ headerFontCase }) => headerFontCase};
  font-weight: ${({ headerFontWeight }) => parseInt(headerFontWeight)};
`;

export const LogoPlaceholder = styled.div<{ isMobile: boolean }>`
  display: flex;
  height: 44px;
  justify-content: center;
  align-items: center;
  padding-top: 5.78px;
  padding-right: 11.56px;
  padding-bottom: 5.78px;
  padding-left: 11.56px;
  border-radius: 4.34px;
  background: ${props => props.theme.colors.secondary.non_alpha};
  border: 1px solid ${props => props.theme.colors.neutral[5]};
  font-family: Work Sans;
  font-weight: 600;
  font-size: 10.84px;
  line-height: 17.34px;
  letter-spacing: 0px;
  color: ${props => props.theme.colors.secondary[100]};
  box-sizing: border-box;

  width: ${({ isMobile }) => (isMobile ? '78px' : '125px')};
`;
export const LogoImage = styled.img<{
  isMobile: boolean;
  headerLogoMaxWidth: number;
  headerLogoMaxHeight: number;
}>`
  object-fit: contain;
  max-width: ${({ headerLogoMaxWidth }) => headerLogoMaxWidth}px;
  max-height: ${({ headerLogoMaxHeight }) => headerLogoMaxHeight}px;

  transform-origin: center left;
  display: flex;
  transform: scale(${({ isMobile }) => (isMobile ? 0.45 : 0.524)});
`;

// BODY
export const BodyContainer = styled.div<{
  values: ILandingPageBuilderForm;
  isMobile: boolean;
  bodyBackgroundColor: string;
  bodyBackgroundImage: IImgProps;
  showSalesPreviewContent: boolean;
}>`
  display: flex;
  box-sizing: border-box;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  flex-shrink: 0;

  height: fit-content;
  padding: ${({ isMobile }) => (isMobile ? '13px' : '40px 0 40px 0')};
  flex-direction: ${({ isMobile }) => (isMobile ? 'column' : 'row')};
  gap: ${({ isMobile }) => (isMobile ? '15px' : '12.5px')};

  background-color: ${({ bodyBackgroundImage, bodyBackgroundColor }) =>
    bodyBackgroundImage?.url ? 'transparent' : bodyBackgroundColor};
  position: relative;
  overflow: hidden;
`;

export const StyledBackgroundImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: -2;
  pointer-events: none;
`;

export const GradientOverlay = styled.div<{ gradient: string }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
  pointer-events: none;

  ${({ gradient }) =>
    gradient === GRADIENT_TYPES.DARK
      ? css`
          background: linear-gradient(
            89.91deg,
            rgba(0, 0, 0, 0.4) 0.09%,
            rgba(0, 0, 0, 0) 99.92%
          );
        `
      : css`
          background: linear-gradient(
            45deg,
            rgba(255, 255, 255, 0.4),
            rgba(255, 255, 255, 0.1)
          );
        `}
`;

export const VideoPlayerImage = styled.img<{ isMobile: boolean }>`
  display: block;
  width: ${({ isMobile }) => (isMobile ? '100%' : '260px')};
  height: ${({ isMobile }) => (isMobile ? '123px' : '147px')};
`;

export const VideoPlayerThumbnail = styled.img<{ isMobile: boolean }>`
  display: block;
  width: 31px;
  height: 31px;
  border-radius: 2px;
`;

export const VideoPlayerContainer = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-direction: column

  width: ${({ isMobile }) => (isMobile ? '100%' : '260px')};
`;

export const ReactionsAndCommentsContainer = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: center;
  border-radius: 4px;
  border-width: 1px;
  background-color: ${props => props.theme.colors.white[100]};
  border: 1px solid ${props => props.theme.colors.neutral[20]};

  height: ${({ isMobile }) => (isMobile ? '54px' : '32px')};
  padding: ${({ isMobile }) => (isMobile ? '0px 44px' : '0px')};
`;

export const AdditionalTextContainer = styled.div<{
  isMobile: boolean;
  additionalTextBackgroundColor: string;
}>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px 28px 8px 19px;
  box-sizing: border-box;
  min-height: 32px;
  border-radius: 4px;
  border-width: 1px;
  border: 1px solid ${props => props.theme.colors.neutral[20]};

  background: ${props => props.additionalTextBackgroundColor};
`;
export const AdditionalText = styled.div<{
  fontFamily: string;
  additionalTextFontFamily: string;
  additionalTextColor: string;
}>`
  font-family: ${({ additionalTextFontFamily }) => additionalTextFontFamily};
  color: ${({ additionalTextColor }) => additionalTextColor};

  * {
    font-family: inherit;
  }

  p {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    margin: 0;
    padding: 0;
    text-align: center;
  }
`;

export const CTAButtonsContainer = styled.div<{ isMobile: boolean }>`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 13px;
`;

export const CtaButton = styled.button<{
  isMobile: boolean;
  buttonColor: string;
  buttonHoverColor: string;
  buttonBorderColor: string;
  buttonBorderHoverColor: string;
  showButtonBorder: number;
  buttonTextColor: string;
  buttonTextHoverColor: string;
  buttonFontCase: FONT_CASE_KEYWORD;
  buttonFontWeight: FONT_WEIGHT_KEYWORD;
  buttonFontSize: number;
  buttonFontFamily: string;
  buttonCornerRadius: number;
  buttonPaddingY: number;
  buttonPaddingX: number;
}>`
  max-height: 48px;
  box-sizing: border-box;
  overflow: hidden;

  display: flex;
  align-items: center;
  justify-content: center;

  font-size: ${({ buttonFontSize }) =>
    `${scaleFontForPreview(buttonFontSize ?? FontSizes[2].value)}px`};
  font-family: ${({ buttonFontFamily }) => buttonFontFamily};
  text-transform: ${({ buttonFontCase }) => buttonFontCase};
  font-weight: ${({ buttonFontWeight }) => parseInt(buttonFontWeight)};
  background: ${({ buttonColor }) =>
    buttonColor ?? LP_DESIGN_COMMON_COLORS.BLACK};
  border: ${({ showButtonBorder, buttonBorderColor }) =>
    showButtonBorder ? `2px solid ${buttonBorderColor}` : 'none'};
  border-radius: ${({ buttonCornerRadius }) => `${buttonCornerRadius}px`};
  padding: ${({ buttonPaddingY, buttonPaddingX }) => {
    const defaultY = 16;
    const defaultX = 32;
    const paddingY = buttonPaddingY != null ? buttonPaddingY : defaultY;
    const paddingX = buttonPaddingX != null ? buttonPaddingX : defaultX;

    return `${paddingY * 0.49}px ${paddingX * 0.49}px`;
  }};

  color: ${({ buttonTextColor }) =>
    buttonTextColor ?? LP_DESIGN_COMMON_COLORS.WHITE};

  &:hover {
    cursor: pointer;
    background: ${({ buttonHoverColor }) => buttonHoverColor};
    border: ${({ showButtonBorder, buttonBorderHoverColor }) =>
      showButtonBorder ? `2px solid ${buttonBorderHoverColor}` : 'none'};
    color: ${({ buttonTextHoverColor }) => buttonTextHoverColor};
  }
`;

// VEHICLE INFO
export const VehicleInformationContainer = styled.div<{ isMobile: boolean }>`
  height: fit-content;
  max-width: ${({ isMobile }) => (isMobile ? '100%' : '167px')};
  padding: 12px;
  box-sizing: border-box;
  border-radius: 4px;
  border-width: 1px;
  background-color: ${props => props.theme.colors.white[100]};
  border: 1px solid ${props => props.theme.colors.neutral[20]};
`;
export const VehicleTitle = styled.div<{}>`
  font-weight: 600;
  font-size: 7px;
  line-height: 4px;
  font-size: 8px;
`;
export const Separator = styled.div`
  height: 1px;
  width: 100%;
  background-color: ${props => props.theme.colors.secondary[5]};
`;
export const VehicleInfoLabel = styled.div`
  color: black;
  color: inherit;
  font-weight: 600;
  font-size: 8px;
  line-height: 8px;
  letter-spacing: 0px;
  width: 43px;
`;
export const VehicleInfoValue = styled.div`
  font-weight: 400;
  line-height: 8px;
  font-size: 7px;
  color: ${props => props.theme.colors.neutral[100]};
`;

// MAP
export const MapInformationContainer = styled.div<{ isMobile: boolean }>`
  height: fit-content;
  max-width: 167px;
  max-width: ${({ isMobile }) => (isMobile ? '100%' : '167px')};
  padding: 12px;
  box-sizing: border-box;
  border-radius: 4px;
  border-width: 1px;
  background-color: ${props => props.theme.colors.white[100]};
  border: 1px solid ${props => props.theme.colors.neutral[20]};
`;
export const MapTitle = styled.div<{}>`
  font-weight: 600;
  font-size: 7px;
  line-height: 4px;
  font-size: 8px;
`;
